// Unpublished Work © 2025-2026 Deere & Company.

import {IconDelete, IconGear, IconShown} from '@deere/icons';
import {Box} from '@mui/material';
import {MANAGE_WORKBOARDS, VIEW_WORKBOARDS} from 'Common/constants/business-activities';
import {WORKBOARD, WORKBOARD_DRAFTS} from 'Common/constants/routes';
import {isAuthorized} from 'Common/utils/authorization-handler';
import moment from 'moment';
import InfoMessage from 'OnLabor/workboard/common/info-message';
import {DRAFT_TYPE, fetchWorkboardDataGrid} from 'OnLabor/workboard/utils/fetch-workboard-data-grid';
import React from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {deleteWorkboard} from 'Services/work-board-service';
import DataGridWrapper from 'Src/ui-core/components/common/data-table/data-grid-wrapper';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {getPresentationUrl} from 'Src/ui-core/features/onlabor/workboards/presentation/utils/presentation-utils';
import {useNavBarActions} from 'Src/ui-core/react-hooks/use-navbar-actions';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {closeDialog, openDialog} from 'Src/ui-core/store/actions/dialogs';
import {redirect} from 'Src/ui-core/utils/redirect-utils';
import {replaceTranslationNames} from 'Src/ui-core/utils/translation-utils';
import PropTypes from 'Utils/prop-type-utils';
import {fetchEffectDataV2} from 'Utils/react-utils';
import dialogTypes from '../../common/dialog-types';
import {DATE_FORMATS} from 'Src/ui-core/utils/time-utils';

function getDraftDialogMessage(message, counter, translations, isLowerCase = false) {
    const draftWording = counter === 1 ? translations.DRAFT : translations.ONLINK_DRAFTS;

    return replaceTranslationNames(message, {
        '0': counter,
        '1': isLowerCase ? draftWording.toLowerCase() : draftWording
    });
}

function getNavBarActions(membershipId, selectedRow, onDeleteDraftClick, history) {
    return [
        {
            disabled: selectedRow.size === 0,
            onClick: onDeleteDraftClick,
            Icon: <IconDelete/>,
            title: 'DELETE',
            requiredMyJdPermissions: MANAGE_WORKBOARDS
        },
        {
            Icon: (
                <IconShown
                    style={{
                        height: '14px',
                        width: '14px'
                    }}
                    viewBox={'4 4 22 22'}
                />
            ),
            onClick: () => window.open(getPresentationUrl(membershipId), '_blank'),
            title: 'ONLINK_VIEW_WORKBOARD',
            variant: 'secondary',
            requiredMyJdPermissions: VIEW_WORKBOARDS
        },
        {
            Icon: <IconGear/>,
            onClick: () => redirect(`${WORKBOARD_DRAFTS}/settings/labor/jobs`, history),
            title: 'SETTINGS',
            variant: 'secondary',
            requiredMyJdPermissions: MANAGE_WORKBOARDS
        }
    ];
}

function getFilterComponent(
    membershipId,
    translations,
    openConfirmation,
    closeConfirmation,
    onDelete,
    selectedDrafts,
    history
) {
    const onDeleteDraftClick = () => {
        openConfirmation({
            onCancel: closeConfirmation,
            showLoadingIcon: true,
            message: getDraftDialogMessage(
                translations.ONLINK_WORKBOARD_TEMPLATES_DRAFTS_CONFIRM_DELETE, selectedDrafts.size, translations, true),
            title: translations.ONLINK_DELETE_DRAFT,
            cancelTitle: translations.CANCEL,
            continueTitle: translations.CONFIRM_DELETE,
            async onContinue() {
                await onDelete();
                closeConfirmation();
            }
        });
    };

    useNavBarActions(
        getNavBarActions(membershipId, selectedDrafts, onDeleteDraftClick, history));
}

function WorkboardDrafts(props) {
    const {
        translations,
        membership,
        openConfirmation,
        closeConfirmation,
        history,
        myJdPermissions
    } = props;

    const {
        ONLINK_NO_DRAFTS_TITLE,
        ONLINK_NO_DRAFTS_BODY
    } = translations;

    const userAuth = {
        myJdPermissions,
        isMigrated: membership.isMigrated
    };

    const {addToast} = useToast();

    const [loading, setLoading] = React.useState(true);
    const [drafts, setDrafts] = React.useState({
        columns: [],
        rows: []
    });
    const [selectedDrafts, setSelectedDrafts] = React.useState(() => new Set());

    const hasManageWorkboards = isAuthorized({
        myJdPermissions: MANAGE_WORKBOARDS
    }, userAuth);

    async function fetchDrafts() {
        setSelectedDrafts(new Set());
        setLoading(true);

        return fetchEffectDataV2(
            async ({signal}) => {
                const res = await fetchWorkboardDataGrid(
                    DRAFT_TYPE,
                    membership.membershipId,
                    translations,
                    signal
                );

                const formattedDrafts = res.rows.map((draft) => ({
                    ...draft,
                    formattedDateSelect: moment(draft.dateSelect, DATE_FORMATS.day),
                    lastModified: moment.utc(draft.lastModified, moment.ISO_8601, true).local()
                }));

                setDrafts({
                    columns: res.columns,
                    rows: formattedDrafts
                });
            },
            {
                onError: () => {
                    addToast({
                        message: translations.REFRESH_BROWSER_AND_TRY_AGAIN,
                        title: translations.ERROR_OCCURRED_TITLE,
                        type: TOAST_TYPE.ERROR
                    });
                    setDrafts({
                        columns: [],
                        rows: []
                    });
                },
                onFinally: () => setLoading(false)
            }
        );
    }

    async function onDelete() {
        let title,
            message,
            type;

        const results = await Promise.allSettled([...selectedDrafts].map((id) => deleteWorkboard(id)));
        const rejectedResults = results.filter(({status}) => status === 'rejected').length;

        if (rejectedResults === 0) {
            title = translations.SUCCESS;
            message = getDraftDialogMessage(
                translations.ONLINK_WORKBOARD_TEMPLATES_DRAFTS_SUCCESS_MESSAGE, selectedDrafts.size, translations).toLowerCase();
            type = TOAST_TYPE.SUCCESS;
        } else if (rejectedResults === selectedDrafts.size) {
            title = getDraftDialogMessage(
                translations.ONLINK_WORKBOARD_TEMPLATES_DRAFTS_ERROR_MESSAGE, selectedDrafts.size, translations);
            message = translations.REFRESH_BROWSER_AND_TRY_AGAIN;
            type = TOAST_TYPE.ERROR;
        } else {
            title = translations.ONLINK_SOME_DRAFT_DELETE_ERROR;
            message = replaceTranslationNames(translations.ONLINK_SOME_DELETE_BODY, {
                '0': rejectedResults,
                '1': selectedDrafts.size,
                '2': translations.ONLINK_DRAFTS.toLowerCase()
            });
            type = TOAST_TYPE.ERROR;
        }

        addToast({
            title,
            message,
            type
        });
        fetchDrafts();
    }

    const filterComponent = getFilterComponent(
        membership.membershipId,
        translations,
        openConfirmation,
        closeConfirmation,
        onDelete,
        selectedDrafts,
        history);

    React.useEffect(() => {
        if (!membership.membershipId) {
            return;
        }

        fetchDrafts();
    }, [membership]);

    return (
        <Box
            sx={{
                m: '15px',
                flexGrow: 1,
                overflow: 'hidden'
            }}
        >
            {
                drafts.rows.length === 0 && !loading ?
                    <InfoMessage
                        body={ONLINK_NO_DRAFTS_BODY}
                        title={ONLINK_NO_DRAFTS_TITLE}
                    /> :
                    <div className='data-grid'>
                        <DataGridWrapper
                            aggregation={{
                                name: {
                                    type: 'size',
                                    config: {
                                        prefix: `${replaceTranslationNames(translations.TOTAL_PLACEHOLDER, {
                                            '0': translations.ONLINK_DRAFTS
                                        })}:`
                                    }
                                }
                            }}
                            columns={drafts.columns}
                            enableDrillIn={hasManageWorkboards}
                            filterComponent={filterComponent}
                            getRowId={(row) => row.workboardId}
                            loading={loading}
                            onDrillIn={(params) => redirect(`${WORKBOARD}/${params.row.workboardId}`, history)}
                            onRowSelectionModelChange={(selectionModel) => setSelectedDrafts(selectionModel.ids)}
                            pinnedColumns={{
                                left: [],
                                right: []
                            }}
                            rowSelectionMode='multiple'
                            rows={drafts.rows}
                            searchFields={['name']}
                            searchable={true}
                            slotProps={{
                                root: {
                                    disableColumnMenu: true,
                                    disableRowSelectionExcludeModel: true
                                }
                            }}
                            translations={translations}
                        />
                    </div>
            }
        </Box>
    );
}

WorkboardDrafts.propTypes = {
    closeConfirmation: PropTypes.func,
    history: PropTypes.history,
    membership: PropTypes.membership,
    myJdPermissions: PropTypes.myJdPermissions,
    openConfirmation: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membership: state.membership,
        myJdPermissions: state.account.myJdPermissions,
        translations: state.translations
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(WorkboardDrafts));
